import { useMemo, type FormEventHandler, type RefObject } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import config from 'constants/config'
import { formatPhoneNumber, sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'
import { PENDING_ACTIONS, type PendingAction } from '../constants'
import { ErrorBanner, PendingButtonContent, renderBoldText, StepBackButton, Title } from './shared'

const NameFields = styled(Group)`
  > label > span {
    margin-bottom: ${rem(8)};
  }
`

interface Props {
  user: App.User
  autoFocus: boolean
  error: any
  processing: boolean
  isEmailSignUp: boolean
  isWhitelabel: boolean
  currentRegionCode: string
  recaptchaRef: RefObject<ReCAPTCHA | null>
  onBack: () => void
  onSubmit: FormEventHandler<HTMLFormElement>
  isActionPending: (action: PendingAction) => boolean
}

function JoinStep({
  user,
  autoFocus,
  error,
  processing,
  isEmailSignUp,
  isWhitelabel,
  currentRegionCode,
  recaptchaRef,
  onBack,
  onSubmit,
  isActionPending,
}: Props) {
  const intl = useIntl()
  const otpSignUpEnabled = config.OTP_SIGN_UP_ENABLED
  const signUpIdentifier = isEmailSignUp
    ? user.email
    : formatPhoneNumber(sanitisePhoneNumber(user.phoneNumber?.phone ?? ''), user.phoneNumber?.prefix ?? '')
  const signUpIntroMessage = useMemo(() => (
    <FormattedMessage
      id="onVHCGv2jmO00Ex8yX-K"
      defaultMessage="Let’s create an account for <b>{identifier}</b>"
      description="Instructional text showing which email or phone number is being registered"
      values={{
        identifier: signUpIdentifier,
        b: renderBoldText,
      }}
    />
  ), [signUpIdentifier])

  return (
    <>
      <StepBackButton
        label={intl.formatMessage({
          id: '_I-T4-bL3TaYsTZhGL9E',
          defaultMessage: 'Back',
          description: 'Back button label for embedded account access chat widget',
        })}
        onClick={onBack}
      />
      <Title>{intl.formatMessage({
        id: 'LFdHXJIv2GHZENoCrG7W',
        defaultMessage: 'Welcome',
        description: 'Modal title for the sign-up form on the login or signup page',
      })}
      </Title>
      <VerticalSpacer as="form" gap={16} onSubmit={onSubmit}>
        <ErrorBanner
          error={error}
          genericMessage={intl.formatMessage({
            id: 'fru8EaNI9dfGrLr7HbmQ',
            defaultMessage: 'Something went wrong. Please try again.',
            description: 'Generic server error message for embedded trip planner account access',
          })}
        />
        <BodyText variant="medium">
          {signUpIntroMessage}
        </BodyText>
        <NameFields direction="vertical" tabletDirection="horizontal" gap={12}>
          <TextInput
            id="givenName"
            required
            type="text"
            name="givenName"
            placeholder={intl.formatMessage({
              id: 'qI3yCJvIefvvd4V0pocL',
              defaultMessage: 'First Name',
              description: 'Input placeholder text for first name input on sign-up form',
            })}
            autoComplete="given-name"
            requiredErrorMessage={intl.formatMessage({
              id: 'yMCDe33AdFwAwPBejTui',
              defaultMessage: 'First name is required',
              description: 'Validation error when first name is empty on sign-up',
            })}
            label={intl.formatMessage({
              id: 'forMsLWoNmexvIDK-mad',
              defaultMessage: 'First name',
              description: 'Form field label for first name input on sign-up form',
            })}
            pattern="^(?=.*[^\\s])[^\\.\\$\\%\\&\\(\\)\\*!,@#\\d]*$"
            invalidErrorMessage={intl.formatMessage({
              id: 'LOLRLNbafZ4Q-AbgS_C3',
              defaultMessage: 'Please enter a valid first name',
              description: 'Validation error when first name contains invalid characters',
            })}
            minLength={2}
            autoFocus={autoFocus}
          />
          <TextInput
            id="surname"
            required
            type="text"
            name="surname"
            placeholder={intl.formatMessage({
              id: 'lkk7qov5lZA6JlCCn2Kp',
              defaultMessage: 'Last Name',
              description: 'Input placeholder text for last name input on sign-up form',
            })}
            autoComplete="family-name"
            requiredErrorMessage={intl.formatMessage({
              id: 'qVk59-TBBAd8ZRg0LfYK',
              defaultMessage: 'Last name is required',
              description: 'Validation error when last name is empty on sign-up',
            })}
            label={intl.formatMessage({
              id: '3MkAhOVLq1mijzgOOUpA',
              defaultMessage: 'Last name',
              description: 'Form field label for last name input on sign-up form',
            })}
            pattern="^(?=.*[^\\s])[^\\.\\$\\%\\&\\(\\)\\*!,@#\\d]*$"
            invalidErrorMessage={intl.formatMessage({
              id: 'BYIvPmEpw57B2auMggj_',
              defaultMessage: 'Please enter a valid last name',
              description: 'Validation error when last name contains invalid characters',
            })}
            minLength={2}
          />
        </NameFields>
        {isEmailSignUp && (
          <>
            <HiddenInput id="email" name="email" value={user.email} />
            <PhoneInput
              name="phoneNumber"
              label={intl.formatMessage({
                id: 'J-Wsp1oRE2INaa5AbraL',
                defaultMessage: 'Phone number',
                description: 'Form field label for phone number input on sign-up form',
              })}
              required={isWhitelabel || otpSignUpEnabled}
              prefixCountry={currentRegionCode}
            />
          </>
        )}
        {!isEmailSignUp && (
          <>
            <TextInput
              label={intl.formatMessage({
                id: 'GUP0HvqiaA2hkG9GWbPN',
                defaultMessage: 'Email address',
                description: 'Label for email input on sign-up form on the login or signup page',
              })}
              id="email"
              required
              type="email"
              name="email"
              autoComplete="email"
              placeholder={intl.formatMessage({
                id: 'ufKjACMx_4cM2-4jxWQc',
                defaultMessage: 'Email',
                description: 'Placeholder for email input on sign-up form when field is empty',
              })}
              requiredErrorMessage={intl.formatMessage({
                id: 'HXRYLWTYYxUBLm7lXHJc',
                defaultMessage: 'Email is required',
                description: 'Form validation error when email is empty on sign-up',
              })}
              defaultValue={user.email}
            />
            <HiddenInput name="phoneNumber.phone" value={user.phoneNumber?.phone} />
            <HiddenInput name="phoneNumber.prefix" value={user.phoneNumber?.prefix} />
          </>
        )}
        {config.signUp.showPostcode && (
          <TextInput
            id="postcode"
            required
            type="text"
            name="postcode"
            placeholder={intl.formatMessage({
              id: 'UHem78pNu9QHQiJi9gNk',
              defaultMessage: 'Postcode',
              description: 'Input placeholder text for the postcode input on sign-up form',
            })}
            autoComplete="off"
            requiredErrorMessage={intl.formatMessage({
              id: 'T4iwKScc-BtUcamNSQuV',
              defaultMessage: 'Postcode is required',
              description: 'Validation error when postcode is empty on sign-up',
            })}
            label={intl.formatMessage({
              id: 'iU-iFc_2311PdXubivlH',
              defaultMessage: 'Postcode',
              description: 'Form field label for the postcode input on sign-up form',
            })}
          />
        )}
        <HiddenInput type="text" />
        <PasswordInput
          id="join-password"
          required
          name="password"
          placeholder={intl.formatMessage({
            id: 'RPUe7V9uAgMjA7jO92en',
            defaultMessage: 'Password',
            description: 'Input placeholder text for the password input on sign-up form',
          })}
          minLength={8}
          autoComplete="new-password"
          requiredErrorMessage={intl.formatMessage({
            id: 'J_lDXjFMbkKkBqwer-Ea',
            defaultMessage: 'Password is required',
            description: 'Validation error when password is empty on sign-up',
          })}
          label={intl.formatMessage({
            id: 'Xg9KC0gHy79s171RPHCp',
            defaultMessage: 'Create password',
            description: 'Form field label for the password input on sign-up form',
          })}
        />
        <CheckboxInput name="agreeTandC" required>
          <AccountAccessLegalText />
        </CheckboxInput>
        <TextButton kind="primary" shape="round" size="medium" fit="flex" type="submit" disabled={processing}>
          <PendingButtonContent
            pending={isActionPending(PENDING_ACTIONS.join)}
            label={intl.formatMessage({
              id: 'CHwOlAdiV0c97glGlCnA',
              defaultMessage: 'Create account',
              description: 'Submit button text for sign-up form on the user account settings page',
            })}
          />
        </TextButton>
        {Boolean(config.RECAPTCHA_KEY) && (
          <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />
        )}
      </VerticalSpacer>
    </>
  )
}

export default JoinStep
